import { FC } from 'react';
import { cn } from '../../utils/cn';

interface FullPageLoaderProps {
  /** Background color class, defaults to bg-gray-50 */
  bgColor?: string;
  /** Dot color class, defaults to bg-yellow-500 */
  dotColor?: string;
  /** Size of dots: sm (2), md (3), lg (4) */
  dotSize?: 'sm' | 'md' | 'lg';
}

const dotSizeClasses = {
  sm: 'w-1.5 h-1.5 sm:w-2 sm:h-2',
  md: 'w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3',
  lg: 'w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4',
};

/**
 * Full page loading indicator with bouncing dots.
 * Used for auth loading states and page transitions.
 */
export const FullPageLoader: FC<FullPageLoaderProps> = ({
  bgColor = 'bg-gray-50 dark:bg-[hsl(220,10%,6%)]',
  dotColor = 'bg-yellow-500',
  dotSize = 'md',
}) => {
  const sizeClass = dotSizeClasses[dotSize];

  return (
    <div className={cn('flex justify-center items-center h-screen', bgColor)}>
      <div className="flex space-x-1 sm:space-x-1.5 md:space-x-2">
        <div
          className={cn(sizeClass, dotColor, 'rounded-full animate-bounce')}
          style={{ animationDelay: '0s' }}
        />
        <div
          className={cn(sizeClass, dotColor, 'rounded-full animate-bounce')}
          style={{ animationDelay: '0.1s' }}
        />
        <div
          className={cn(sizeClass, dotColor, 'rounded-full animate-bounce')}
          style={{ animationDelay: '0.2s' }}
        />
      </div>
    </div>
  );
};

export default FullPageLoader;
